import type { ApiErrorBody, Me } from '#shared';

/** Error with a message that's already written for the end user (from the API or below). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields: Record<string, string> = {},
    /** Extra context from the API, e.g. the existing lead for a duplicate warning. */
    public readonly data?: Record<string, unknown>,
  ) {
    super(message);
  }
}

// The access token lives only in memory (never localStorage), so injected scripts can't read it later.
let accessToken: string | null = null;
let onSession: ((me: Me | null) => void) | null = null;
let onUnavailable: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

export function setSessionListener(fn: (me: Me | null) => void) {
  onSession = fn;
}

/** Called when the server can't be reached or is busy (the login itself may still be fine). */
export function setUnavailableListener(fn: () => void) {
  onUnavailable = fn;
}

export function acceptSession(token: string, me: Me) {
  accessToken = token;
  onSession?.(me);
}

export function clearSession() {
  accessToken = null;
  onSession?.(null);
}

/** Uses the httpOnly refresh cookie to get a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'same-origin' });
      // Only the server saying "this login is over" signs the person out. A restart, a busy server or a
      // bad connection must not: the login is still valid and works again in a moment.
      if (res.status === 401 || res.status === 403) {
        // Signed out because they signed in on another device: the sign-in page explains.
        const code = ((await res.json().catch(() => null)) as ApiErrorBody | null)?.error?.code;
        if (code === 'SIGNED_IN_ELSEWHERE') try { sessionStorage.setItem('aicrm-signed-out', 'elsewhere'); } catch { /* private mode */ }
        clearSession();
        return false;
      }
      if (!res.ok) {
        onUnavailable?.();
        return false;
      }
      const body = (await res.json()) as { accessToken: string; me: Me };
      acceptSession(body.accessToken, body.me);
      return true;
    } catch {
      onUnavailable?.();
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const send = () =>
    fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res: Response;
  try {
    res = await send();
    if (res.status === 401 && !path.startsWith('/auth/login') && (await refreshSession())) res = await send();
  } catch {
    throw new ApiError(0, 'NETWORK', 'Can’t reach the server. Please check your internet connection and try again.');
  }

  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? 'Something went wrong. Please try again.',
      err?.fields ?? {},
      err?.data,
    );
  }
  return data as T;
}

/** Downloads a file from the API with the user's login (e.g. an Excel export) and saves it. */
export async function downloadFile(path: string, fallbackName: string, body?: unknown) {
  const send = () =>
    fetch(`/api/v1${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) {
    const err = ((await res.json().catch(() => null)) as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'UNKNOWN', err?.message ?? 'The download failed. Please try again.');
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Uploads one file (multipart) with the user's login. */
export async function uploadFile<T>(path: string, file: File | Blob, fileName = 'file', fields: Record<string, string> = {}): Promise<T> {
  const send = () => {
    const form = new FormData();
    // Text fields first: the server reads them before the file.
    for (const [k, v] of Object.entries(fields)) form.append(k, v);
    form.append('file', file, (file as File).name ?? fileName);
    return fetch(`/api/v1${path}`, { method: 'POST', credentials: 'same-origin', headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}, body: form });
  };
  let res: Response;
  try {
    res = await send();
    if (res.status === 401 && (await refreshSession())) res = await send();
  } catch {
    throw new ApiError(0, 'NETWORK', 'Can’t reach the server. Please check your internet connection and try again.');
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'UNKNOWN', err?.message ?? 'Upload failed. Please try again.', err?.fields ?? {});
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
